import { IBasicRightResults } from "UI/UI/Rights/BasicRightsResults";
import { RightType } from "../../../../Global/Base/Rights/RightTypes";
import { ActionType } from "../../../UI.Controls/Actions/IAction";
import { ILogicalComponent } from "../../LogicalTree/ILogicalComponent";
import { RightsEvaluationOverrides } from "../../Rights/RightsEvaluationOverrides";
import { IEditableControl } from "../Controls/ControlInterfaces";
import { EditableControl } from "../Controls/EditableControl";
import { FileEdit } from "../Controls/FileEdit";
import { StandardAction } from "./Actions/StandardAction";
import { DataEventLabel } from "./DataEventLabel";
import { EditStyleType, IEditStyleMasterableFrame, IMultiRecordsComponent } from "./Frames/FrameInterfaces";
import { IDetailFrame } from "./Frames/IDetailMasterableFrame";
import { IEditableFrame } from "./Frames/IEditableFrame";
import { IFrame } from "./IFrame";

/**
 * Overrides pro DetailFrame
 */
export class DetailFrameRightsOverrides extends RightsEvaluationOverrides {
    protected override readonly __frame: IEditableFrame;

    protected override get _frame(): IEditableFrame {
        return this.__frame;
    }

    private get _detailFrame(): IDetailFrame {
        return this.__frame instanceof IDetailFrame ? this.__frame : null;
    }

    protected override _initializeOverrides(): void {
        super._initializeOverrides();

        this._registerOverrideFunctionForRightType(EditableControl, RightType.enabled, this._editableControlsEnabled, true);
        this._registerOverrideFunctionForRightType(FileEdit, RightType.enabled, this._editableFileEditEnabled, true);
        this._registerOverrideFunctionForRightType(DataEventLabel, RightType.visible, this._dataEventLabelVisible);

        // TODO Prava pro EditableControlConnector - v budoucnu by se mel sloucit s EditableControl, aby se nehandloval zvlast Control/EditableControl a ControlConnector/EditableControlConnector.
        this._registerInterfaceType(IEditableControl);
        this._registerOverrideFunctionForRightType(IEditableControl, RightType.enabled, this._editableControlsEnabled, true);
    }

    protected override _standardActionsInsert(controlWrapper: ILogicalComponent): boolean | null {
        const standardAction = controlWrapper.control as StandardAction;
        if (standardAction && standardAction.actionType === ActionType.standardNewRecord) {
            return this._frame.actualObjectStandardRights.canNew && this._frame.interactive && (this._frame.masterFrame == null || this._frame.masterFrame.masterFrame == null || this._frame.masterFrame.masterFrame.actualObject != null);
        } else if (standardAction && standardAction.actionType === ActionType.standardCopyRecord && this._frame instanceof IFrame) {
            return this._frame.hasSelectedRecord && this._frame.interactive && this._frame.actualObjectStandardRights.canNew && !this._frame.actualObject.isServerSideEncrypted;
        }

        return null;
    }
    protected override async _standardActionsVisible(controlWrapper: ILogicalComponent): Promise<boolean | null> {
        const standardAction = controlWrapper.control as StandardAction;

        switch (standardAction.actionType) {
            case ActionType.standardNewRecord:
                //pokud má detail mastera, potřebujeme vědět jestli není v režimu výběru více záznamů a jestli podporuje multi editaci
                if (this._frame.masterFrame) {
                    const isMultiSelectingMode = this._frame.masterFrame instanceof IMultiRecordsComponent && this._frame.masterFrame.isMultiSelectingMode;
                    const isMultiEditingSupported = this._frame.masterFrame instanceof IEditStyleMasterableFrame && this._frame.masterFrame.editStyle == EditStyleType.esMulti;
                    return (isMultiEditingSupported || !this._frame.isEditing) && !isMultiSelectingMode;
                }
                break;
            case ActionType.standardDeleteRecords:
            case ActionType.standardEditRecords:
                return (this._frame instanceof IMultiRecordsComponent) ? this._frame.isMultiSelectingMode && !this._frame.isEditing : false;
            case ActionType.standardSaveRecord:
            case ActionType.standardSaveAll:
            case ActionType.standardCancelChanges:
            case ActionType.standardCancelRecordChanges:
                if (!!this._frame.isEditing || (this._frame.masterFrame?.isEditContextInitiator && this._frame.useMasterContext)) {
                    return true;
                }
                break;
            case ActionType.standardSaveRecordInEditContext:
                return (await this._frame.supportsEditContext) && !!this._frame.isEditing && (this._frame.hasHandedContext || this._frame.useMasterContext || this._frame.masterFrame?.hasHandedContext);
        }
        return await super._standardActionsVisible(controlWrapper);
    }

    protected _editableControlsEnabled(controlWrapper: ILogicalComponent): boolean | null {
        if (controlWrapper.control && !this._frame.isInitialized && (this._frame.actualObject === null || this._frame.masterFrame === null)) {
            return false;
        }

        return null;
    }

    protected _editableFileEditEnabled(controlWrapper: ILogicalComponent): boolean | null {
        return controlWrapper.control && !this._frame.actualObject ? false : null;
    }

    protected override async _standardActionsEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): Promise<boolean | null> {
        const superResult = super._standardActionsEnabled(controlWrapper, basicRightResult, allBasicRightsResult);

        const standardAction = controlWrapper.control as StandardAction;

        if (standardAction) {
            switch (standardAction.actionType) {
                case ActionType.standardRefreshList:
                case ActionType.standardRefreshRecord:
                    if (this._frame.masterFrame && !this._frame.masterObject) {
                        return this._frame.actualObject && !this._frame.isEditing;
                    } else {
                        return !this._frame.isEditing;
                    }
                case ActionType.standardNewRecord:
                    if (this._frame.masterFrame && !this._frame.masterObject) {
                        if ((this._frame.masterFrame.canInsert === false && !this._detailFrame?.ignoreMasterRightsObject)
                            || this._frame.actualObjectStandardRights.canNew === false) {
                            return false;
                        }
                        if (this._frame.actualObject) {
                            return !this._frame.isEditing || (this._frame.masterFrame instanceof IEditStyleMasterableFrame && this._frame.masterFrame.editStyle == EditStyleType.esMulti);
                        }
                    }
                    return this._frame.interactive && (!this._frame.isEditing || (this._frame.masterFrame instanceof IEditStyleMasterableFrame && this._frame.masterFrame.editStyle == EditStyleType.esMulti));
                case ActionType.standardSaveRecord:
                case ActionType.standardSaveAll:
                case ActionType.standardCancelChanges:
                case ActionType.standardCancelRecordChanges:
                    if (!!this._frame.isEditing || (this._frame.masterFrame?.isEditContextInitiator && this._frame.useMasterContext)) {
                        return true;
                    }
                    break;
                case ActionType.standardSaveRecordInEditContext:
                    return (await this._frame.supportsEditContext) && !!this._frame.isEditing && (this._frame.hasHandedContext || this._frame.useMasterContext || this._frame.masterFrame?.hasHandedContext);
            }
        }

        return superResult;
    }

    protected _dataEventLabelVisible(_controlWrapper: ILogicalComponent, _basicRightResult: boolean | null, allBasicRightsResult: IBasicRightResults): boolean | null {
        // komponenta DataEventLabel se skrývá pokud je její metoda zakázaná právy
        return allBasicRightsResult.get(RightType.enabled);
    }
}
